// Ask brief PDF. Server-rendered, text-based document (selectable text,
// real headings, page numbers) via the render-ask-pdf edge function.
import { supabase } from '@/lib/supabase';
import type { AskResult } from '@/lib/admin/insights-api';

function slugify(s: string) {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || 'ask-brief'
  );
}

export async function downloadAskPdf(opts: {
  result: AskResult;
  question: string;
  filenameSlug?: string;
  scopeLabel?: string;
}): Promise<void> {
  const { result, question, scopeLabel = 'Ask brief' } = opts;

  const { data, error } = await supabase.functions.invoke('render-ask-pdf', {
    body: {
      question,
      generated_at: result.generated_at,
      headline: result.headline ?? null,
      confidence: result.confidence ?? null,
      situation_markdown: result.situation_markdown ?? result.answer_markdown ?? '',
      why_it_matters_markdown: result.why_it_matters_markdown ?? null,
      drivers_markdown: result.drivers_markdown ?? null,
      considerations_markdown: result.considerations_markdown ?? null,
      action_implications_markdown: result.action_implications_markdown ?? null,
      evidence_coverage_markdown: result.evidence_coverage_markdown ?? null,
      evidence_markdown: result.situation_markdown ? null : result.evidence_markdown ?? null,
      chart: result.chart ?? null,
      insights: result.insights ?? [],
      knowledge_empty: result.knowledge_empty ?? false,
      citations: result.citations ?? [],
      sources: result.sources ?? [],
      scope_label: scopeLabel,
    },
  });
  if (error) throw error;

  const blob =
    data instanceof Blob ? data : new Blob([data as BlobPart], { type: 'application/pdf' });
  if (blob.type && blob.type.includes('json')) {
    const text = await blob.text();
    throw new Error(JSON.parse(text).error ?? 'PDF generation failed');
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `culturelens-${slugify(opts.filenameSlug ?? question)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
